import { useQuery } from "@tanstack/react-query";
import type { AxiosRequestConfig } from "axios";
import { unauthHttpRequest } from "@fmc-digital/auth-api";
import type { DefaultObject } from "@fmc-digital/auth-api";
import { useTokenReadiness } from "./useTokenReadiness";

/**
 * Fetch server time from T1 gateway
 * Returns server timestamp to avoid relying on client time
 */
const fetchServerTime = async (signal?: AbortSignal): Promise<number> => {
  try {
    const config: AxiosRequestConfig = {
      url: `/servertime?isHeadersPassed=true`,
      method: "GET",
      signal
    };

    const response = await unauthHttpRequest<DefaultObject>(config);
    
    // API returns { currentDateTime: "2026-03-10T15:30:00Z" } or similar
    const serverTimeStr = response?.currentDateTime as string;
    
    if (serverTimeStr) {
      const serverTime = new Date(serverTimeStr).getTime();
      
      // Validate the timestamp is reasonable (not NaN and not too far in past/future)
      if (!isNaN(serverTime) && serverTime > 0) {
        return serverTime;
      }
      
      console.warn('[ServerTime] Invalid server time value:', serverTimeStr, serverTime);
    }
    
    // Fallback to local time if server doesn't return valid time
    console.warn('[ServerTime] No valid server time in response, using local time');
    return Date.now();
  } catch (error) {
    console.warn('[ServerTime] Failed to fetch server time:', error);
    return Date.now();
  }
};

/**
 * Hook to get server time from T1 gateway
 * 
 * This follows the servicing-login pattern of fetching server time
 * to avoid client time zone and clock skew issues
 */
export function useServerTime(enabled: boolean = true) {
  const { isTokenReady, hasBaseUrlt1 } = useTokenReadiness();

  const { data: serverTime, isLoading, error } = useQuery({
    queryKey: ["server-time"],
    queryFn: ({ signal }) => fetchServerTime(signal),
    enabled: enabled && hasBaseUrlt1 && isTokenReady, // Only enable when token is ready
    staleTime: 60 * 1000, // 1 minute - refresh server time periodically
    refetchInterval: 60 * 1000, // Refetch every minute to keep time in sync
    retry: 1,
  });

  // Ensure we never return NaN - use Date.now() as ultimate fallback
  const safeServerTime = serverTime && !isNaN(serverTime) 
    ? serverTime 
    : Date.now();

  return {
    serverTime: safeServerTime,
    isLoading,
    error,
    hasServerTime: !!serverTime && !isNaN(serverTime),
  };
}
